import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Animated, StyleSheet } from 'react-native'

import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@zet/common/components/toast'
import { useModal } from '@zet/common/hooks/useModal'
import { B5, Button, colors, Stack, XStack } from '@zet/design-system'
import { i18n } from '@zet/localization'
import { navigateTo } from '@zet/utils/navigation'
import {
  Banner,
  CancellationConfirmationModal,
  CancelSubscription,
  Faqs,
  Header,
  HowItWorks,
  PaymentFailure,
  PrimeExpired,
  ProductsIncluded,
  useSubscriptionStore,
} from '@zet/zet-prime'

import {
  initiatePayment,
  retrySubscriptionStatus,
} from '../../api/landingPage/functions'
import {
  useLandingPageContent,
  useSubscribeZetPrime,
  useSubscriptionInfo,
  useSubscriptionStatus,
} from '../../api/landingPage/hooks'
import { DEFAULT_LANDING_PAGE_CONTENT } from '../../api/landingPage/types'
import MissingEmail from '../../components/bottomSheets/missingEmail'
import { SUBSCRIPTION_STATUS } from '../../utils/constants'

const ZetPrime = () => {
  const [showMissingEmail, setShowMissingEmail] = useState<boolean>(false)
  const { data: apiData, isLoading } = useLandingPageContent()
  const { mutate: subscribeZetPrime, isLoading: isSubscribeZetPrimeLoading } =
    useSubscribeZetPrime()
  const { subscription } = useSubscriptionStore()
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const { refetch } = useSubscriptionInfo()
  const queryClient = useQueryClient()
  const backButtonAnimatedValue = useRef(new Animated.Value(0)).current
  const [
    isCancelSubscriptionSheetVisible,
    setIsCancelSubscriptionSheetVisible,
  ] = useState(false)
  const { openModal, closeAllModals } = useModal()

  const { mutate: getSubscriptionStatus } = useSubscriptionStatus()

  const [
    isCancellationConfirmationModalVisible,
    setIsCancellationConfirmationModalVisible,
  ] = useState(false)

  const [isPaymentFailureVisible, setIsPaymentFailureVisible] = useState(false)
  const [isPrimeExpiredVisible, setIsPrimeExpiredVisible] = useState(false)

  // Check if subscription is expired on mount
  useEffect(() => {
    if (subscription?.status === SUBSCRIPTION_STATUS.EXPIRED) {
      setIsPrimeExpiredVisible(true)
    }
  }, [subscription?.status])

  const data = useMemo(
    () => ({
      ...DEFAULT_LANDING_PAGE_CONTENT,
      ...apiData,
    }),
    [apiData],
  )

  const transformedData = useMemo(() => {
    return {
      howItWorksSteps: data.howItWorks.benefits.map((benefit) => benefit.text),
      products: data.zetPrimeProducts.map((product) => ({
        image: product.url,
      })),
      faqs: data.faqs.map(({ id, ...rest }) => rest),
    }
  }, [data])

  const onSubscribePress = () => {
    if (subscription?.status === SUBSCRIPTION_STATUS.ACTIVE) {
      navigateTo('HomeView', { screen: 'Categories' })
      return
    }
    setLoading(true)
    subscribeZetPrime(subscription?.status === SUBSCRIPTION_STATUS.EXPIRED, {
      onSuccess: async (data: any) => {
        if (data?.success && data?.token) {
          try {
            await initiatePayment(data?.token)
            openModal('ZetPrimePaymentModal', {
              title: 'Payment in Progress',
              subTitle:
                "Your payment might take some time. Please don't go back or close this tab.",
            })
            // Retry subscription status API 3 times with 3-second intervals
            await retrySubscriptionStatus(
              data?.transactionId,
              getSubscriptionStatus,
              () => {
                queryClient.invalidateQueries({
                  queryKey: ['zet-prime', 'subscription-info'],
                })
                refetch()
              },
            )
            // Payment successful
            closeAllModals()
            setLoading(false)
            navigateTo('ZetPrimeSuccessScreen')
          } catch (error: any) {
            // Show payment failure bottom sheet after all retries failed
            setLoading(false)
            closeAllModals()
            setIsPaymentFailureVisible(true)
          }
        }
      },
      onError: (error: unknown) => {
        closeAllModals()
        if (error?.response?.data?.error?.code === 4000) {
          setShowMissingEmail(true)
        } else if (
          error?.response?.data?.error?.code === 400 &&
          error?.response?.data?.error?.message ===
            'Your transaction is in progress. Please wait for sometime.'
        ) {
          toast?.show({
            intent: 'CautionOutlined',
            heading: 'Notification',
            hideAfter: 7000,
            description:
              (error?.response?.data?.error?.message as string) ??
              i18n.t('toastMessages.somethingWentWrong'),
          })
        } else {
          toast?.show({
            intent: 'error',
            heading: i18n.t('toastMessages.error'),
            hideAfter: 3000,
            description:
              (error?.response?.data?.error?.message as string) ??
              i18n.t('toastMessages.somethingWentWrong'),
          })
        }

        setLoading(false)
      },
    })
  }

  if (isLoading) {
    return (
      <ActivityIndicator
        color={colors.primary[100]}
        size="large"
        style={styles.loading}
      />
    )
  }

  const onCancellationModalClose = () => {
    setIsCancellationConfirmationModalVisible(false)
  }

  const getButtonText = () => {
    if (subscription?.status === SUBSCRIPTION_STATUS.ACTIVE)
      return i18n.t('zetPrime.sellNow')

    return i18n.t('zetPrime.subscribeNow')
  }

  const onEmailClose = ({ success }: { success: boolean }) => {
    setShowMissingEmail(false)

    if (success) {
      onSubscribePress()
    }
  }

  if (isLoading) {
    return (
      <ActivityIndicator
        color={colors.primary[100]}
        size="large"
        style={styles.loading}
      />
    )
  }

  return (
    <Stack className="flex-1">
      <Header
        backButtonAnimatedValue={backButtonAnimatedValue}
        setIsCancelSubscriptionSheetVisible={
          setIsCancelSubscriptionSheetVisible
        }
      />

      <Animated.ScrollView
        style={styles.container}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: backButtonAnimatedValue } } }],
          {
            useNativeDriver: true,
          },
        )}
        scrollEventThrottle={16}
      >
        <Banner
          banner={data.bannerImage}
          bannerText={data.bannerText}
          bannerStrip={data.bannerStrip}
        />
        <HowItWorks
          title={data.howItWorks.title}
          steps={transformedData.howItWorksSteps}
        />
        <ProductsIncluded products={transformedData.products} />
        <Faqs faqs={transformedData.faqs} title={i18n.t('zetPrime.faqs')} />
      </Animated.ScrollView>
      <Stack className="w-full bg-white p-3">
        <Button
          className="w-full"
          size="lg"
          onPress={onSubscribePress}
          loading={loading || isSubscribeZetPrimeLoading}
          disabled={loading || isSubscribeZetPrimeLoading}
        >
          {getButtonText()}
        </Button>
        {!subscription?.status && (
          <XStack className="justify-center mt-2">
            <B5 className={`text-black font-normal`}>
              {i18n.t('zetPrime.billedMonthly', {
                amount: data.amount,
              })}{' '}
            </B5>
            <B5>{i18n.t('zetPrime.cancelAnytime')}</B5>
          </XStack>
        )}
      </Stack>

      {isCancelSubscriptionSheetVisible && (
        <CancelSubscription
          isVisible={isCancelSubscriptionSheetVisible}
          setIsCancellationConfirmationModalVisible={
            setIsCancellationConfirmationModalVisible
          }
          onClose={() => setIsCancelSubscriptionSheetVisible(false)}
        />
      )}

      {isCancellationConfirmationModalVisible && (
        <CancellationConfirmationModal
          isVisible={isCancellationConfirmationModalVisible}
          onClose={onCancellationModalClose}
          subscriptionEndDate={'15 Oct 2025'}
        />
      )}

      <PaymentFailure
        isVisible={isPaymentFailureVisible}
        onClose={() => setIsPaymentFailureVisible(false)}
        onRetry={onSubscribePress}
      />

      <PrimeExpired
        isVisible={isPrimeExpiredVisible}
        onClose={() => setIsPrimeExpiredVisible(false)}
        onSubscribe={onSubscribePress}
      />

      <MissingEmail isVisible={showMissingEmail} onClose={onEmailClose} />
    </Stack>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
})

export default ZetPrime
